// @ts-check
// Configurações > Modelo de dados (doc 11, 3.6.1 e 3.6.3): lista de objetos ativos e desativados e criação de objeto.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { computeMetadataNameFromLabel } from '../../../../../shared/metadata/metadata-names.js';
import { Link, navigate } from '../../../app/router.js';
import { createObjectMetadata } from '../../../data/data-model.js';
import { allObjectsState, isTenantModuleEnabled } from '../../../data/metadata-store.js';
import { withoutDisabledModuleMetadata } from '../../../lib/module-metadata-visibility.js';
import { normalizeSearchText } from '../../../lib/utils.js';
import { Button } from '../../../ui/button.js';
import { Tag } from '../../../ui/chip.js';
import { Icon } from '../../../ui/icon.js';
import { Toggle } from '../../../ui/feedback.js';
import { showSuccessSnackbar } from '../../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../../i18n/index.js';
import { PageCard, PageHeader } from '../../page/page-card.js';
import { SettingsSection } from '../settings-pages.js';
import { DataModelBreadcrumb, canEditDataModel, rootBreadcrumbItem, showDataModelError } from './data-model-shared.js';
import { IconPicker } from './pickers.js';

/** @param {import('../../../lib/field-metadata.js').ObjectMetadata} object */
const objectKindLabel = (object) => (object.isSystem ? t('settings.objects.system') : object.isCustom ? t('settings.objects.custom') : t('settings.objects.standard'));

/**
 * @param {{ objects: import('../../../lib/field-metadata.js').ObjectMetadata[] }} props
 */
function ObjectsTable({ objects }) {
  return html`<div class="settings-table">
    <div class="settings-table-row settings-table-header" data-columns="objects">
      <span>${t('settings.objects.columns.name')}</span>
      <span>${t('settings.objects.columns.type')}</span>
      <span class="settings-table-number">${t('settings.objects.columns.fields')}</span>
      <span></span>
    </div>
    ${objects.map((objectMetadata) => html`<${Link} key=${objectMetadata.id} href=${`/settings/objects/${objectMetadata.namePlural}`} className="settings-table-row" data-columns="objects">
      <span class="settings-table-name"><${Icon} name=${objectMetadata.icon ?? 'IconCube'} size=${16} />${translateMetadataLabel(objectMetadata.labelPlural)}
        ${objectMetadata.isActive === false ? html`<${Tag} color="gray" text=${t('dataModel.common.deactivated')} variant="outline" />` : null}</span>
      <span class="settings-table-muted">${objectKindLabel(objectMetadata)}</span>
      <span class="settings-table-number">${objectMetadata.fields?.filter((field) => !field.isSystem).length ?? 0}</span>
      <span class="settings-table-chevron"><${Icon} name="IconChevronRight" size=${16} /></span>
    <//>`)}
  </div>`;
}

export function SettingsObjectsPage() {
  const [search, setSearch] = useState('');
  const [showSystem, setShowSystem] = useState(false);
  useEffect(() => {
    document.title = `${t('dataModel.title')} - ${t('navigation.settings')}`;
  }, []);
  const normalizedSearch = normalizeSearchText(search);
  // objetos de módulo desligado nem aparecem entre os inativos (a escola sem o módulo vê a 2.0.0)
  const visible = withoutDisabledModuleMetadata(allObjectsState.value, isTenantModuleEnabled)
    .filter((object) => showSystem || !object.isSystem)
    .filter((object) => !normalizedSearch || normalizeSearchText(translateMetadataLabel(object.labelPlural)).includes(normalizedSearch))
    .sort((first, second) => translateMetadataLabel(first.labelPlural).localeCompare(translateMetadataLabel(second.labelPlural)));
  const active = visible.filter((object) => object.isActive !== false);
  const inactive = visible.filter((object) => object.isActive === false);
  const actions = canEditDataModel()
    ? html`<${Button} title=${t('dataModel.objects.add')} icon="IconPlus" accent="blue" size="small" onClick=${() => navigate('/settings/objects/new')} />`
    : null;
  return html`<${PageCard} header=${html`<${PageHeader} icon="IconHierarchy2" title=${t('dataModel.title')} actions=${actions} />`}>
    <div class="settings-page">
      <${SettingsSection} title=${t('dataModel.objects.title')} description=${t('dataModel.objects.description')}>
        <div class="data-model-toolbar">
          <label class="data-model-search">
            <${Icon} name="IconSearch" size=${16} />
            <input class="text-input" type="search" value=${search} placeholder=${t('dataModel.objects.search')} aria-label=${t('dataModel.objects.search')}
              onInput=${(event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
          <label class="data-model-toggle-label">
            <${Toggle} value=${showSystem} onChange=${setShowSystem} ariaLabel=${t('dataModel.objects.showSystem')} size="small" />
            <span>${t('dataModel.objects.showSystem')}</span>
          </label>
        </div>
        <${ObjectsTable} objects=${active} />
        ${active.length === 0 ? html`<p class="data-model-empty-hint">${t('common.noResults')}</p>` : null}
      <//>
      ${inactive.length > 0
        ? html`<${SettingsSection} title=${t('dataModel.objects.inactiveTitle')} description=${t('dataModel.objects.inactiveDescription')}>
            <${ObjectsTable} objects=${inactive} />
          <//>`
        : null}
      <${SettingsSection} title=${t('dataModel.funnel.title')} description=${t('dataModel.funnel.shortDescription')}>
        <div><${Button} title=${t('dataModel.funnel.open')} icon="IconLayoutKanban" variant="secondary" size="small" onClick=${() => navigate('/settings/funnel')} /></div>
      <//>
    </div>
  <//>`;
}

export function SettingsObjectNewPage() {
  const [icon, setIcon] = useState('IconListNumbers');
  const [labelSingular, setLabelSingular] = useState('');
  const [labelPlural, setLabelPlural] = useState('');
  const [description, setDescription] = useState('');
  const [isNameSynced, setIsNameSynced] = useState(true);
  const [nameSingular, setNameSingular] = useState('');
  const [namePlural, setNamePlural] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    document.title = `${t('dataModel.objects.newTitle')} - ${t('navigation.settings')}`;
  }, []);

  const apiNameSingular = isNameSynced ? computeMetadataNameFromLabel(labelSingular) : nameSingular;
  const apiNamePlural = isNameSynced ? computeMetadataNameFromLabel(labelPlural) : namePlural;
  const takenNames = new Set(allObjectsState.value.flatMap((object) => [object.nameSingular, object.namePlural]));
  let error = null;
  if (labelSingular.trim() && labelPlural.trim()) {
    if (!/^[a-z][a-zA-Z0-9]*$/.test(apiNameSingular) || !/^[a-z][a-zA-Z0-9]*$/.test(apiNamePlural)) error = t('dataModel.errors.invalidApiName');
    else if (apiNameSingular === apiNamePlural) error = t('dataModel.errors.sameNames');
    else if (takenNames.has(apiNameSingular) || takenNames.has(apiNamePlural)) error = t('dataModel.errors.objectExists');
  }
  const canSave = Boolean(labelSingular.trim() && labelPlural.trim() && !error) && canEditDataModel();

  const save = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      const created = await createObjectMetadata({
        nameSingular: apiNameSingular, namePlural: apiNamePlural, labelSingular: labelSingular.trim(), labelPlural: labelPlural.trim(),
        description: description.trim() || undefined, icon,
      });
      showSuccessSnackbar(t('dataModel.objects.created'));
      navigate(`/settings/objects/${created?.namePlural ?? apiNamePlural}`, { replace: true });
    } catch (saveError) {
      showDataModelError(saveError);
      setIsSaving(false);
    }
  };

  const breadcrumb = html`<${DataModelBreadcrumb} items=${[rootBreadcrumbItem(), { label: t('dataModel.objects.newTitle') }]} />`;
  const actions = html`<div class="data-model-header-actions">
    <${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${() => navigate('/settings/objects')} />
    <${Button} title=${t('common.save')} icon="IconCheck" accent="blue" size="small" disabled=${!canSave} isLoading=${isSaving} onClick=${save} />
  </div>`;
  return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} actions=${actions} />`}>
    <form class="settings-page" onSubmit=${(/** @type {Event} */ event) => {
      event.preventDefault();
      save();
    }}>
      <${SettingsSection} title=${t('dataModel.objects.aboutTitle')} description=${t('dataModel.objects.aboutDescription')}>
        <div class="data-model-name-row">
          <${IconPicker} value=${icon} onChange=${setIcon} />
          <label class="form-field">
            <span class="form-field-label">${t('dataModel.objects.labelSingular')}</span>
            <input class="text-input" value=${labelSingular} placeholder=${t('dataModel.objects.labelSingularPlaceholder')} autofocus maxlength="255"
              onInput=${(event) => setLabelSingular(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
          <label class="form-field">
            <span class="form-field-label">${t('dataModel.objects.labelPlural')}</span>
            <input class="text-input" value=${labelPlural} placeholder=${t('dataModel.objects.labelPluralPlaceholder')} maxlength="255"
              onInput=${(event) => setLabelPlural(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
        </div>
        <label class="form-field">
          <span class="form-field-label">${t('dataModel.common.description')}</span>
          <textarea class="text-input data-model-textarea" value=${description} placeholder=${t('dataModel.common.descriptionPlaceholder')}
            onInput=${(event) => setDescription(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
        </label>
      <//>
      <${SettingsSection} title=${t('dataModel.common.apiNamesTitle')} description=${t('dataModel.objects.apiNamesDescription')}>
        <label class="data-model-toggle-label">
          <${Toggle} value=${isNameSynced} ariaLabel=${t('dataModel.objects.syncNames')} size="small" onChange=${(value) => {
            if (!value) {
              setNameSingular(apiNameSingular);
              setNamePlural(apiNamePlural);
            }
            setIsNameSynced(value);
          }} />
          <span>${t('dataModel.objects.syncNames')}</span>
        </label>
        <div class="form-field-row">
          <label class="form-field">
            <span class="form-field-label">${t('dataModel.objects.apiNameSingular')}</span>
            <input class="text-input settings-table-mono" value=${apiNameSingular} disabled=${isNameSynced} maxlength="63"
              onInput=${(event) => setNameSingular(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
          <label class="form-field">
            <span class="form-field-label">${t('dataModel.objects.apiNamePlural')}</span>
            <input class="text-input settings-table-mono" value=${apiNamePlural} disabled=${isNameSynced} maxlength="63"
              onInput=${(event) => setNamePlural(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
        </div>
        ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
      <//>
    </form>
  <//>`;
}
